/**
 * P4-1: чистая логика построения before/after списков оборудования стойки
 * из текущего состояния и AI-items. Тестируется в rackDiff.logic.test.mjs.
 */
import type { WorkOrderItemPreview } from '../../types/workOrder'
import { uHeightOrDefault } from './aiPreview.logic'

export interface RackEquipmentLike {
  id: string
  name: string
  equipment_type: string
  u_height: number
  u_position: number
  weight_kg: number
  attributes?: unknown
}

export interface RackDiff {
  before: RackEquipmentLike[]
  after: RackEquipmentLike[]
  addedCount: number
  removedCount: number
}

/** rack_id всех items, затрагивающих стойки (для селектора при нескольких стойках). */
export function affectedRackIds(items: WorkOrderItemPreview[]): number[] {
  const ids = new Set<number>()
  for (const item of items) {
    const rackId = Number(item.metadata?.rack_id)
    if (Number.isFinite(rackId) && rackId > 0) ids.add(rackId)
  }
  return [...ids]
}

/**
 * Строит diff одной стойки: before = текущее оборудование,
 * after = before − REMOVE items + INSTALL items (виртуальные, с префиксом «+»).
 * Удаляемые в before помечаются префиксом «−» (canvas сам не умеет подсветку).
 */
export function buildRackDiff(
  current: RackEquipmentLike[],
  items: WorkOrderItemPreview[],
  rackId: number,
): RackDiff {
  const removedIds = new Set<string>()
  const removedNames: string[] = []
  const installed: RackEquipmentLike[] = []

  for (const item of items) {
    const meta = item.metadata ?? {}
    if (item.item_type === 'INSTALL_EQUIPMENT' && Number(meta.rack_id) === rackId) {
      const uPos = Number(meta.position_u ?? meta.u_position)
      if (!Number.isFinite(uPos) || uPos < 1) continue
      installed.push({
        id: `ai-new-${installed.length}`,
        name: `+ ${String(
          meta.catalog_id
            ? (meta.resolved_catalog_name || meta.equipment_name || item.description)
            : item.description
        ).slice(0, 40)}`,
        equipment_type: String(meta.equipment_type || 'server'),
        u_height: uHeightOrDefault(meta.u_height),
        u_position: uPos,
        weight_kg: 0,
      })
    } else if (item.item_type === 'REMOVE_EQUIPMENT') {
      if (meta.equipment_id) removedIds.add(String(meta.equipment_id))
      const name = String(meta.equipment_name ?? '').trim().toLowerCase()
      if (name) removedNames.push(name)
    }
  }

  const isRemoved = (eq: RackEquipmentLike) =>
    removedIds.has(eq.id) || removedNames.some(n => eq.name.toLowerCase().includes(n))

  const before = current.map(eq => (isRemoved(eq) ? { ...eq, name: `− ${eq.name}` } : eq))
  const kept = current.filter(eq => !isRemoved(eq))
  const after = [...kept, ...installed]

  return {
    before,
    after,
    addedCount: installed.length,
    removedCount: current.length - kept.length,
  }
}

/** U-позиции, занятые списком оборудования (для occupiedPositions канваса). */
export function occupiedPositions(list: RackEquipmentLike[], uSlots: number): number[] {
  const occupied = new Set<number>()
  for (const eq of list) {
    for (let u = eq.u_position; u < eq.u_position + uHeightOrDefault(eq.u_height); u++) {
      if (u >= 1 && u <= uSlots) occupied.add(u)
    }
  }
  return [...occupied].sort((a, b) => a - b)
}
